import { useState, type SyntheticEvent } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { readFamilySignature, type SignatureView } from '../api/signature-view.js';
import type { Person } from '../api/use-people.js';
import { Disclosure } from './disclosure.js';
import { SignatureCodeLine } from './signature-codes.js';
import { SignatureRows } from './signature-rows.js';
import { WaitNote } from './wait-note.js';

/**
 * A child's Performance Signature on their page (stage 4b): closed, the codes;
 * opened in place, a row per section, each the page the athlete sees. Read
 * when first opened, so the page itself never waits for it. Nothing until the
 * coach has entered a code.
 */
export function ChildSignature({ person, fromHome }: { person: Person; fromHome: boolean }) {
  const getToken = useAccessToken();
  const [view, setView] = useState<SignatureView | null>(null);
  const [failed, setFailed] = useState(false);

  if (!person.signature) return null;

  function opened(event: SyntheticEvent<HTMLDetailsElement>) {
    if (!event.currentTarget.open || view) return;
    setFailed(false);
    readFamilySignature(getToken, person.client_id)
      .then((found) => setView(found.signature))
      .catch(() => setFailed(true));
  }

  return (
    <Disclosure title="Performance Signature" summary={<SignatureCodeLine codes={person.signature} />} onToggle={opened}>
      {failed ? <p className="warn-note">This did not load. Try again.</p> : null}
      {!failed && !view ? <WaitNote>Loading…</WaitNote> : null}
      {view ? <SignatureRows view={view} base={`/account/people/${person.client_id}/signature`} query={fromHome ? '?from=home' : ''} /> : null}
    </Disclosure>
  );
}
